import type { ComponentType } from 'react';
import { Button, ErrorState, LoadingState, Modal } from '@/components/ui';
import { getPharmacy, useQuery } from '@/data';
import { ActivityLogModal } from './modals/ActivityLogModal';
import { ApiIntegrationModal } from './modals/ApiIntegrationModal';
import { ApplicationUpdateModal } from './modals/ApplicationUpdateModal';
import { DevicesModal } from './modals/DevicesModal';
import { EditPharmacyModal } from './modals/EditPharmacyModal';
import { ManageLicenseModal } from './modals/ManageLicenseModal';
import { MobileAccessModal } from './modals/MobileAccessModal';
import { ACTION_META } from './actionMeta';
import type { ActionModalProps, PharmacyAction, PharmacyRef } from './types';

const MODALS: Record<PharmacyAction, ComponentType<ActionModalProps>> = {
  license: ManageLicenseModal,
  update: ApplicationUpdateModal,
  api: ApiIntegrationModal,
  devices: DevicesModal,
  mobile: MobileAccessModal,
  edit: EditPharmacyModal,
  activity: ActivityLogModal,
};

interface ActionModalHostProps {
  action: PharmacyAction;
  pharmacy: PharmacyRef;
  onClose: () => void;
}

export function ActionModalHost({ action, pharmacy, onClose }: ActionModalHostProps) {
  const { data, error, loading, reload } = useQuery(() => getPharmacy(pharmacy.id), [pharmacy.id]);
  const meta = ACTION_META[action];
  const Icon = meta.icon;

  if (data) {
    const Component = MODALS[action];
    return <Component pharmacy={data} onClose={onClose} />;
  }

  return (
    <Modal open onClose={onClose} title={meta.label} description={pharmacy.name} icon={<Icon size={18} aria-hidden="true" />} size={meta.size}>
      {loading && <LoadingState label="Loading pharmacy…" />}
      {error && (
        <ErrorState
          message={error.message}
          action={
            <Button variant="secondary" onClick={reload}>
              Try again
            </Button>
          }
        />
      )}
    </Modal>
  );
}
